import React, { useState, useEffect } from 'react';
import { mockOrders, mockDonations, mockAuth } from '../services/mockDatabase';
import { Trash2, ShoppingCart, CreditCard, Sparkles, CheckCircle } from 'lucide-react';

const Cart = ({ cart, onUpdateQuantity, onRemoveFromCart, onClearCart }) => {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ donationPercentage: 0.10 });
  const [checkoutStep, setCheckoutStep] = useState('cart'); // 'cart' | 'checkout' | 'success'
  
  // Checkout form inputs
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [shippingAddress, setShippingAddress] = useState({
    line1: '',
    city: '',
    state: '',
    postal_code: ''
  });
  
  const [error, setError] = useState('');
  const [placedOrder, setPlacedOrder] = useState(null);

  useEffect(() => {
    setUser(mockAuth.getCurrentUser());
    setStats(mockDonations.getDonationStats());
    
    // Auto-fill shipping address if user has one
    const currentUser = mockAuth.getCurrentUser();
    if (currentUser && currentUser.shippingAddress) {
      setShippingAddress(currentUser.shippingAddress);
    }
  }, []);

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal > 0 ? 3.50 : 0;
  const donationCut = subtotal * stats.donationPercentage;
  const total = subtotal + shipping;

  const handleStartCheckout = () => {
    if (!user) {
      setError('Please sign in to place an order.');
      setTimeout(() => {
        window.location.hash = '#/login';
      }, 1500);
      return;
    }
    setCheckoutStep('checkout');
  };

  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Basic Mock Validation
    if (cardNumber.replace(/\s/g, '').length !== 16) {
      setError('Invalid credit card number. Use test card 4242 4242 4242 4242.');
      return;
    }

    try {
      // Save shipping address to user profile
      mockAuth.updateProfile(user.displayName, shippingAddress);
      
      // Create Order
      const newOrder = mockOrders.createOrder(cart, total);
      setPlacedOrder(newOrder);
      
      // Clear Cart
      onClearCart();
      setCheckoutStep('success');
    } catch (err) {
      setError(err.message);
    }
  };

  if (checkoutStep === 'success') {
    return (
      <div className="max-w-md mx-auto my-16 p-8 bg-white rounded-3xl shadow-xl text-center border border-pink-100">
        <div className="inline-flex p-4 bg-green-100 text-green-500 rounded-full mb-4 animate-bounce">
          <CheckCircle size={48} />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">Order Placed!</h2>
        <p className="text-gray-500 mt-2">Thank you for your order, {user?.displayName}!</p>
        
        <div className="my-6 p-4 bg-pink-50/50 rounded-2xl border border-pink-100">
          <div className="flex justify-between items-center text-sm font-semibold text-gray-600 mb-2">
            <span>Order Number:</span>
            <span className="text-gray-800 font-mono font-bold">{placedOrder?.id}</span>
          </div>
          <div className="flex justify-between items-center text-sm font-semibold text-gray-600 mb-2">
            <span>Stripe Payment Status:</span>
            <span className="text-green-600 font-bold">Paid / Confirmed</span>
          </div>
          <div className="flex justify-between items-center text-sm font-semibold text-gray-600 border-t border-pink-100 pt-2 mt-2">
            <span>Donation to St. Jude:</span>
            <span className="text-pink-600 font-bold">${placedOrder?.donationAmount.toFixed(2)}</span>
          </div>
        </div>

        <p className="text-xs text-gray-400 mb-6">
          A portion of this purchase was successfully designated to help support St. Jude Children's Research Hospital.
        </p>

        <a
          href="#/profile"
          className="block w-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-3 rounded-xl transition shadow"
        >
          Track My Order
        </a>
      </div>
    );
  }

  if (checkoutStep === 'checkout') {
    return (
      <div className="max-w-2xl mx-auto my-12 p-6 md:p-8 bg-white rounded-3xl shadow-xl border border-pink-50">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <CreditCard className="text-pink-500" /> Stripe Secure Checkout
        </h2>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm mb-6 border border-red-100 font-semibold">
            {error}
          </div>
        )}

        <form onSubmit={handlePaymentSubmit} className="space-y-6">
          {/* Shipping Details */}
          <div>
            <h3 className="font-bold text-gray-700 text-sm mb-3 uppercase tracking-wider">Shipping Address</h3>
            <div className="space-y-3">
              <input
                type="text"
                required
                value={shippingAddress.line1}
                onChange={(e) => setShippingAddress({ ...shippingAddress, line1: e.target.value })}
                placeholder="Street Address"
                className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="text"
                  required
                  value={shippingAddress.city}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                  placeholder="City"
                  className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                />
                <input
                  type="text"
                  required
                  value={shippingAddress.state}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                  placeholder="State"
                  className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                />
                <input
                  type="text"
                  required
                  value={shippingAddress.postal_code}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, postal_code: e.target.value })}
                  placeholder="Zip"
                  className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-gray-700 text-sm uppercase tracking-wider">Payment Details</h3>
              <span className="text-xs text-gray-400 font-semibold">Stripe Test Mode</span>
            </div>
            <div className="space-y-3 p-4 bg-gray-50 rounded-2xl border border-gray-200">
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Card Number</label>
                <input
                  type="text"
                  required
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim())}
                  placeholder="4242 4242 4242 4242"
                  maxLength={19}
                  className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all bg-white font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Expires (MM/YY)</label>
                  <input
                    type="text"
                    required
                    value={expiry}
                    onChange={(e) => setExpiry(e.target.value)}
                    placeholder="12/28"
                    maxLength={5}
                    className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all bg-white text-center font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">CVC</label>
                  <input
                    type="text"
                    required
                    value={cvc}
                    onChange={(e) => setCvc(e.target.value.replace(/\D/g, ''))}
                    placeholder="123"
                    maxLength={3}
                    className="w-full p-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all bg-white text-center font-mono"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Checkout Totals Summary */}
          <div className="p-4 bg-pink-50/30 rounded-2xl border border-pink-100 text-sm">
            <div className="flex justify-between mb-1">
              <span className="text-gray-500">Order Total:</span>
              <span className="font-bold text-gray-700">${total.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs text-pink-600 font-semibold border-t border-pink-100 pt-1 mt-1">
              <span>Includes St. Jude Donation Cut:</span>
              <span>${donationCut.toFixed(2)}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setCheckoutStep('cart')}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition text-sm"
            >
              Back to Cart
            </button>
            <button
              type="submit"
              className="flex-1 bg-pink-500 hover:bg-pink-600 text-white font-bold py-3 rounded-xl shadow-md hover:shadow-lg transition text-sm"
            >
              Pay ${total.toFixed(2)}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto my-12 px-6">
      <h2 className="text-3xl font-extrabold text-gray-800 mb-8 flex items-center gap-2">
        <ShoppingCart className="text-pink-500" /> Your Shopping Cart
      </h2>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm mb-6 border border-red-100 font-semibold text-center">
          {error}
        </div>
      )}

      {cart.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl shadow-sm border border-gray-100">
          <p className="text-gray-400 italic mb-4">Your cart is empty.</p>
          <a
            href="#/"
            className="inline-block bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-2.5 rounded-full shadow transition"
          >
            Go to Shop
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Cart Items List */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map(item => (
              <div 
                key={item.id} 
                className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 hover:shadow-md transition-shadow"
              >
                {/* Photo Placeholder */}
                <div className="w-20 h-20 bg-gray-100 rounded-xl overflow-hidden shrink-0 flex items-center justify-center text-gray-300 font-semibold text-xs">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <span>[ Photo ]</span>
                  )}
                </div>

                <div className="flex-1">
                  <h4 className="font-bold text-gray-800 leading-tight">{item.name}</h4>
                  
                  {/* Customized details */}
                  {item.customOptions && (
                    <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                      {item.customOptions.letters && (
                        <div>Word/Name: <span className="font-bold text-purple-600">{item.customOptions.letters.toUpperCase()}</span></div>
                      )}
                      {item.customOptions.colorScheme && item.customOptions.colorScheme.length > 0 && (
                        <div>Colors: <span className="font-semibold">{item.customOptions.colorScheme.join(', ')}</span></div>
                      )}
                      {item.customOptions.notes && (
                        <div className="italic">Note: "{item.customOptions.notes}"</div>
                      )}
                    </div>
                  )}

                  <p className="text-pink-600 font-bold text-sm mt-1">${item.price.toFixed(2)}</p>
                </div>

                {/* Quantity Controls */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onUpdateQuantity(item.id, Math.max(1, item.quantity - 1))}
                    className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center font-bold text-gray-500 hover:bg-gray-50"
                  >
                    -
                  </button>
                  <span className="w-6 text-center text-sm font-bold text-gray-800">{item.quantity}</span>
                  <button
                    onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                    className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center font-bold text-gray-500 hover:bg-gray-50"
                  >
                    +
                  </button>
                </div>

                {/* Remove button */}
                <button
                  onClick={() => onRemoveFromCart(item.id)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>

          {/* Cart Pricing Summary Panel */}
          <div className="bg-white p-6 rounded-3xl shadow-lg border border-pink-50 h-fit space-y-6">
            <h3 className="font-bold text-gray-800 text-lg border-b border-gray-100 pb-3">Summary</h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal:</span>
                <span className="font-bold text-gray-800">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Shipping:</span>
                <span className="font-bold text-gray-800">${shipping.toFixed(2)}</span>
              </div>
              <div className="border-t border-gray-100 pt-3 flex justify-between font-bold text-gray-800 text-base">
                <span>Total:</span>
                <span className="text-pink-600">${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Donation Widget Tally Preview */}
            <div className="p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl border border-pink-100 flex gap-3 items-center">
              <span className="text-xl">💝</span>
              <div className="text-xs">
                <span className="block font-bold text-pink-700">St. Jude Donation: ${donationCut.toFixed(2)}</span>
                <span className="block text-gray-500 mt-0.5">{(stats.donationPercentage * 100).toFixed(0)}% of proceeds will go directly to support St. Jude Hospital.</span>
              </div>
            </div>

            <button
              onClick={handleStartCheckout}
              className="w-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-3.5 rounded-xl shadow-md hover:shadow-lg transition-transform hover:-translate-y-0.5 active:scale-95 flex items-center justify-center gap-2 text-sm"
            >
              <CreditCard size={18} /> Checkout via Stripe
            </button>

            <button
              onClick={onClearCart}
              className="w-full text-center text-xs text-gray-400 hover:text-gray-600 font-semibold"
            >
              Clear Cart
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

export default Cart;
